import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, Send, Paperclip, X, Trash2, CheckCircle, Cloud, Plus, Flag, ArrowLeft, Clock, Edit, Sparkles, FileText, MessageSquare, Calendar, Video, Key } from 'lucide-react';
import { gmailAPI, gmailImapAPI, apiCall, contactsAPI, scheduledEmailsAPI, documentsAPI, meetingsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Account configuration
const ACCOUNTS = [
  { id: 'creatrhq', name: 'CreatrHQ', email: 'creatrhq@gmail.com', color: 'orange', type: 'gmail' },
  { id: 'creatrverse', name: 'Creatrverse', email: 'creatrverse@gmail.com', color: 'purple', type: 'gmail' },
  { id: 'twenty8', name: 'Twenty8 Holdings', email: 'darren@twenty8holdings.com', color: 'blue', type: 'outlook2' }
];

// MOCKUP EMAIL DATA GENERATOR
const getMockupEmails = (accountId, folder) => {
  const baseEmails = {
    creatrhq: {
      inbox: [
        {
          id: 'mock-creatrhq-1',
          thread_id: 'thread-1',
          subject: 'Q1 Marketing Strategy Review',
          from: 'Sarah Mitchell <sarah.mitchell@company.com>',
          sender: 'sarah.mitchell@company.com',
          sender_name: 'Sarah Mitchell',
          to: 'creatrhq@gmail.com',
          body: 'Hi Team,\n\nI wanted to schedule a meeting to review our Q1 marketing strategy and discuss the upcoming campaign launches. Would next Tuesday at 2pm work for everyone?\n\nBest regards,\nSarah',
          body_preview: 'I wanted to schedule a meeting to review our Q1 marketing strategy and discuss the upcoming campaign launches...',
          received_time: 'Dec 29, 2025 at 10:30 AM',
          received_datetime: '2025-12-29T10:30:00Z',
          is_read: false,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-2',
          thread_id: 'thread-2',
          subject: 'Partnership Opportunity - Tech Conference 2026',
          from: 'David Chen <d.chen@techevent.com>',
          sender: 'd.chen@techevent.com',
          sender_name: 'David Chen',
          to: 'creatrhq@gmail.com',
          body: 'Hello,\n\nI am reaching out from TechEvent 2026. We are organizing the largest tech conference next year and would love to explore partnership opportunities with CreatrHQ.\n\nCould we schedule a call to discuss this further?\n\nDavid',
          body_preview: 'I am reaching out from TechEvent 2026. We are organizing the largest tech conference next year...',
          received_time: 'Dec 28, 2025 at 3:15 PM',
          received_datetime: '2025-12-28T15:15:00Z',
          is_read: false,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-3',
          thread_id: 'thread-3',
          subject: 'Introduction: Emma Williams - Product Designer',
          from: 'Michael Thompson <michael@designstudio.com>',
          sender: 'michael@designstudio.com',
          sender_name: 'Michael Thompson',
          to: 'creatrhq@gmail.com',
          body: 'Hi there,\n\nI wanted to introduce you to Emma Williams, an exceptional product designer who has worked with several Fortune 500 companies. I think there could be some great synergy between her work and your projects.\n\nEmma - meet the CreatrHQ team.\nCreatrHQ - meet Emma.\n\nLooking forward to seeing great things happen!\n\nMichael',
          body_preview: 'I wanted to introduce you to Emma Williams, an exceptional product designer who has worked with several Fortune 500...',
          received_time: 'Dec 28, 2025 at 11:00 AM',
          received_datetime: '2025-12-28T11:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-4',
          thread_id: 'thread-4',
          subject: 'URGENT: Server Maintenance Tonight',
          from: 'IT Department <it@creatrhq.com>',
          sender: 'it@creatrhq.com',
          sender_name: 'IT Department',
          to: 'creatrhq@gmail.com',
          body: 'URGENT NOTICE\n\nPlanned server maintenance will occur tonight from 11 PM to 2 AM EST. All services will be temporarily unavailable during this window.\n\nPlease save your work and log out before 11 PM.\n\nThank you for your cooperation.',
          body_preview: 'URGENT NOTICE - Planned server maintenance will occur tonight from 11 PM to 2 AM EST...',
          received_time: 'Dec 27, 2025 at 4:00 PM',
          received_datetime: '2025-12-27T16:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-5',
          thread_id: 'thread-5',
          subject: 'Holiday Travel Confirmation',
          from: 'TravelBookings <noreply@travelbookings.com>',
          sender: 'noreply@travelbookings.com',
          sender_name: 'TravelBookings',
          to: 'creatrhq@gmail.com',
          body: 'Your flight booking has been confirmed!\n\nFlight: AA 1234\nFrom: New York (JFK) to San Francisco (SFO)\nDate: January 15, 2026\nDeparture: 8:00 AM\nArrival: 11:30 AM\n\nBooking Reference: ABC123XYZ\n\nHave a great trip!',
          body_preview: 'Your flight booking has been confirmed! Flight AA 1234 from New York to San Francisco...',
          received_time: 'Dec 27, 2025 at 9:00 AM',
          received_datetime: '2025-12-27T09:00:00Z',
          is_read: true,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-6',
          thread_id: 'thread-6',
          subject: 'Design Review Session Tomorrow',
          from: 'Rachel Green <rachel@designteam.com>',
          sender: 'rachel@designteam.com',
          sender_name: 'Rachel Green',
          to: 'creatrhq@gmail.com',
          body: 'Hi everyone,\n\nReminder that we have our design review session tomorrow at 3 PM. Please have your mockups ready for presentation.\n\nSee you there!\nRachel',
          body_preview: 'Reminder that we have our design review session tomorrow at 3 PM...',
          received_time: 'Dec 27, 2025 at 8:00 AM',
          received_datetime: '2025-12-27T08:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-7',
          thread_id: 'thread-7',
          subject: 'Collaboration Request - AI Integration',
          from: 'Alex Rodriguez <alex@aitech.com>',
          sender: 'alex@aitech.com',
          sender_name: 'Alex Rodriguez',
          to: 'creatrhq@gmail.com',
          body: 'Hello,\n\nI came across your work on AI-powered applications and I am impressed! We are building a similar product and would love to explore collaboration opportunities.\n\nWould you be open to a quick 15-minute intro call?\n\nCheers,\nAlex',
          body_preview: 'I came across your work on AI-powered applications and I am impressed...',
          received_time: 'Dec 26, 2025 at 5:30 PM',
          received_datetime: '2025-12-26T17:30:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-8',
          thread_id: 'thread-8',
          subject: 'Newsletter: December Product Updates',
          from: 'Product Team <product@creatrhq.com>',
          sender: 'product@creatrhq.com',
          sender_name: 'Product Team',
          to: 'creatrhq@gmail.com',
          body: 'December Product Updates\n\n• New dashboard analytics\n• Performance improvements\n• Bug fixes\n• Mobile app beta release\n\nRead more on our blog!',
          body_preview: 'December Product Updates: New dashboard analytics, Performance improvements, Bug fixes...',
          received_time: 'Dec 26, 2025 at 10:00 AM',
          received_datetime: '2025-12-26T10:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-9',
          thread_id: 'thread-9',
          subject: 'Legal Review - Vendor Contract',
          from: 'Legal Department <legal@creatrhq.com>',
          sender: 'legal@creatrhq.com',
          sender_name: 'Legal Department',
          to: 'creatrhq@gmail.com',
          body: 'We have completed the legal review of the vendor contract. Minor changes have been made to sections 4 and 7. Please review and approve.\n\nAttached: Vendor_Contract_Final_v2.pdf',
          body_preview: 'We have completed the legal review of the vendor contract. Minor changes have been made...',
          received_time: 'Dec 25, 2025 at 3:00 PM',
          received_datetime: '2025-12-25T15:00:00Z',
          is_read: true,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-10',
          thread_id: 'thread-10',
          subject: 'Customer Feedback Summary - Q4 2025',
          from: 'Customer Success <cs@creatrhq.com>',
          sender: 'cs@creatrhq.com',
          sender_name: 'Customer Success',
          to: 'creatrhq@gmail.com',
          body: 'Q4 Customer Feedback Summary:\n\nPositive: 87%\nNeutral: 10%\nNegative: 3%\n\nTop requests:\n1. Dark mode\n2. API documentation\n3. Mobile app\n\nFull report attached.',
          body_preview: 'Q4 Customer Feedback Summary: Positive 87%, Neutral 10%, Negative 3%...',
          received_time: 'Dec 25, 2025 at 9:00 AM',
          received_datetime: '2025-12-25T09:00:00Z',
          is_read: true,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-11',
          thread_id: 'thread-11',
          subject: 'Team Offsite Planning - January 2026',
          from: 'HR Team <hr@creatrhq.com>',
          sender: 'hr@creatrhq.com',
          sender_name: 'HR Team',
          to: 'creatrhq@gmail.com',
          body: 'We are planning a team offsite for January 2026! Please fill out the survey to let us know your preferences:\n\n• Dates that work for you\n• Location preferences\n• Activity suggestions\n\nSurvey link: teamoffsite.com/survey',
          body_preview: 'We are planning a team offsite for January 2026! Please fill out the survey...',
          received_time: 'Dec 24, 2025 at 2:00 PM',
          received_datetime: '2025-12-24T14:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-creatrhq-12',
          thread_id: 'thread-12',
          subject: 'Weekly Security Report',
          from: 'Security Team <security@creatrhq.com>',
          sender: 'security@creatrhq.com',
          sender_name: 'Security Team',
          to: 'creatrhq@gmail.com',
          body: 'Weekly Security Report\n\n• No critical vulnerabilities detected\n• 2 software updates required\n• Password policy compliance: 98%\n• Suspicious login attempts blocked: 47\n\nAll systems secure.',
          body_preview: 'Weekly Security Report: No critical vulnerabilities detected, 2 software updates required...',
          received_time: 'Dec 24, 2025 at 8:00 AM',
          received_datetime: '2025-12-24T08:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        }
      ],
      sent: [
        {
          id: 'mock-creatrhq-sent-1',
          thread_id: 'thread-sent-1',
          subject: 'Re: Q1 Budget Proposal',
          from: 'CreatrHQ <creatrhq@gmail.com>',
          sender: 'creatrhq@gmail.com',
          sender_name: 'CreatrHQ',
          to: 'finance@company.com',
          body: 'Thank you for the detailed budget breakdown. Everything looks good. We approve the Q1 budget as proposed.\n\nBest regards,\nCreatrHQ Team',
          body_preview: 'Thank you for the detailed budget breakdown. Everything looks good...',
          received_time: 'Dec 29, 2025 at 9:00 AM',
          received_datetime: '2025-12-29T09:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        }
      ],
      starred: [
        {
          id: 'mock-creatrhq-star-1',
          thread_id: 'thread-star-1',
          subject: 'Annual Performance Review - 2025',
          from: 'HR Department <hr@creatrhq.com>',
          sender: 'hr@creatrhq.com',
          sender_name: 'HR Department',
          to: 'creatrhq@gmail.com',
          body: 'Your annual performance review is scheduled for January 10, 2026. Please prepare your self-assessment and achievements list.\n\nWe look forward to discussing your growth and future goals!',
          body_preview: 'Your annual performance review is scheduled for January 10, 2026...',
          received_time: 'Dec 26, 2025 at 2:00 PM',
          received_datetime: '2025-12-26T14:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: ['starred'],
          source: 'mockup',
          account_id: accountId
        }
      ]
    },
    darren: {
      inbox: [
        {
          id: 'mock-darren-1',
          thread_id: 'thread-d-1',
          subject: 'Investor Meeting - Next Steps',
          from: 'Jennifer Park <j.park@vcfund.com>',
          sender: 'j.park@vcfund.com',
          sender_name: 'Jennifer Park',
          to: 'darren@creatrverse.com',
          body: 'Hi Darren,\n\nGreat meeting yesterday! Our team is excited about the opportunity. I am attaching our term sheet for your review.\n\nLet me know when you are available for a follow-up discussion.\n\nBest,\nJennifer',
          body_preview: 'Great meeting yesterday! Our team is excited about the opportunity...',
          received_time: 'Dec 29, 2025 at 11:15 AM',
          received_datetime: '2025-12-29T11:15:00Z',
          is_read: false,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-2',
          thread_id: 'thread-d-2',
          subject: 'Speaking Opportunity at Tech Summit 2026',
          from: 'Conference Organizers <events@techsummit.com>',
          sender: 'events@techsummit.com',
          sender_name: 'Conference Organizers',
          to: 'darren@creatrverse.com',
          body: 'Dear Darren,\n\nWe would be honored to have you as a keynote speaker at Tech Summit 2026. Your insights on AI and entrepreneurship would be invaluable to our audience of 5,000+ attendees.\n\nWould you be available for June 15-17, 2026?\n\nLooking forward to your response!',
          body_preview: 'We would be honored to have you as a keynote speaker at Tech Summit 2026...',
          received_time: 'Dec 28, 2025 at 2:30 PM',
          received_datetime: '2025-12-28T14:30:00Z',
          is_read: false,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-3',
          thread_id: 'thread-d-3',
          subject: 'Weekly Team Sync - Agenda',
          from: 'Team Lead <team@creatrverse.com>',
          sender: 'team@creatrverse.com',
          sender_name: 'Team Lead',
          to: 'darren@creatrverse.com',
          body: 'Hi Darren,\n\nAgenda for this week meeting:\n1. Product roadmap updates\n2. Engineering challenges\n3. Marketing campaign results\n4. Budget allocation for Q1\n\nMeeting is scheduled for Wednesday at 10 AM.\n\nSee you there!',
          body_preview: 'Agenda for this weeks meeting: Product roadmap updates, Engineering challenges...',
          received_time: 'Dec 28, 2025 at 9:00 AM',
          received_datetime: '2025-12-28T09:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-4',
          thread_id: 'thread-d-4',
          subject: 'Podcast Interview Request',
          from: 'Lisa Anderson <lisa@techpodcast.com>',
          sender: 'lisa@techpodcast.com',
          sender_name: 'Lisa Anderson',
          to: 'darren@creatrverse.com',
          body: 'Hi Darren,\n\nI host a tech podcast with 50K+ weekly listeners. Would you be interested in being a guest to discuss your entrepreneurial journey?\n\nWe record remotely and sessions are typically 45 minutes.\n\nLet me know!\nLisa',
          body_preview: 'I host a tech podcast with 50K+ weekly listeners. Would you be interested in being a guest...',
          received_time: 'Dec 27, 2025 at 6:00 PM',
          received_datetime: '2025-12-27T18:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-5',
          thread_id: 'thread-d-5',
          subject: 'Introduction to Sarah Martinez - Angel Investor',
          from: 'Robert Kim <robert@network.com>',
          sender: 'robert@network.com',
          sender_name: 'Robert Kim',
          to: 'darren@creatrverse.com',
          body: 'Darren,\n\nI want to connect you with Sarah Martinez, an angel investor who is actively looking for AI startups to invest in. I think Creatrverse would be a great fit.\n\nSarah, Darren is building something incredible in the AI space.\n\nI will let you two take it from here!\nRobert',
          body_preview: 'I want to connect you with Sarah Martinez, an angel investor who is actively looking for AI startups...',
          received_time: 'Dec 27, 2025 at 1:00 PM',
          received_datetime: '2025-12-27T13:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-6',
          thread_id: 'thread-d-6',
          subject: 'Press Coverage - Forbes Article',
          from: 'Media Relations <pr@creatrverse.com>',
          sender: 'pr@creatrverse.com',
          sender_name: 'Media Relations',
          to: 'darren@creatrverse.com',
          body: 'Great news! Forbes is featuring Creatrverse in their upcoming article "Top 10 AI Startups to Watch in 2026".\n\nThey would like to schedule a quick call with you tomorrow for additional quotes.\n\nCongratulations!',
          body_preview: 'Great news! Forbes is featuring Creatrverse in their upcoming article Top 10 AI Startups...',
          received_time: 'Dec 27, 2025 at 10:00 AM',
          received_datetime: '2025-12-27T10:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-7',
          thread_id: 'thread-d-7',
          subject: 'Board Meeting Prep - January 5',
          from: 'Board Secretary <board@creatrverse.com>',
          sender: 'board@creatrverse.com',
          sender_name: 'Board Secretary',
          to: 'darren@creatrverse.com',
          body: 'Reminder: Board meeting on January 5 at 9 AM.\n\nPlease prepare:\n• Q4 financial results\n• Product roadmap 2026\n• Hiring plan\n• Customer metrics\n\nBoard deck due by January 3.',
          body_preview: 'Reminder: Board meeting on January 5 at 9 AM. Please prepare Q4 financial results...',
          received_time: 'Dec 26, 2025 at 4:00 PM',
          received_datetime: '2025-12-26T16:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-darren-8',
          thread_id: 'thread-d-8',
          subject: 'Customer Success Metrics - December',
          from: 'Analytics Team <analytics@creatrverse.com>',
          sender: 'analytics@creatrverse.com',
          sender_name: 'Analytics Team',
          to: 'darren@creatrverse.com',
          body: 'December Customer Metrics:\n\n• Active Users: 25,000 (+15%)\n• Churn Rate: 2.1%\n• NPS Score: 72\n• Customer Satisfaction: 4.6/5\n\nFull report attached.',
          body_preview: 'December Customer Metrics: Active Users 25,000, Churn Rate 2.1%, NPS Score 72...',
          received_time: 'Dec 26, 2025 at 8:00 AM',
          received_datetime: '2025-12-26T08:00:00Z',
          is_read: true,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        }
      ]
    },
    wdjwood: {
      inbox: [
        {
          id: 'mock-wdj-1',
          thread_id: 'thread-w-1',
          subject: 'Project Proposal - Mobile App Development',
          from: 'Client Services <client@devagency.com>',
          sender: 'client@devagency.com',
          sender_name: 'Client Services',
          to: 'wdjwood@gmail.com',
          body: 'Hello,\n\nWe have reviewed your portfolio and would like to discuss a mobile app development project with an estimated budget of $150,000.\n\nAre you available for a discovery call this week?\n\nBest regards,\nClient Services Team',
          body_preview: 'We have reviewed your portfolio and would like to discuss a mobile app development project...',
          received_time: 'Dec 29, 2025 at 1:45 PM',
          received_datetime: '2025-12-29T13:45:00Z',
          is_read: false,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-wdj-2',
          thread_id: 'thread-w-2',
          subject: 'Code Review Request - Feature Branch',
          from: 'Dev Team <dev@company.com>',
          sender: 'dev@company.com',
          sender_name: 'Dev Team',
          to: 'wdjwood@gmail.com',
          body: 'Hi,\n\nCould you review the PR for the new authentication feature? It is ready for your feedback.\n\nPR Link: github.com/company/repo/pull/123\n\nThanks!',
          body_preview: 'Could you review the PR for the new authentication feature...',
          received_time: 'Dec 28, 2025 at 4:20 PM',
          received_datetime: '2025-12-28T16:20:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-wdj-3',
          thread_id: 'thread-w-3',
          subject: 'Contract Renewal - AWS Services',
          from: 'AWS Support <support@aws.amazon.com>',
          sender: 'support@aws.amazon.com',
          sender_name: 'AWS Support',
          to: 'wdjwood@gmail.com',
          body: 'Your AWS contract is up for renewal on January 15, 2026.\n\nCurrent plan: Enterprise\nMonthly cost: $2,500\n\nTo review or modify your plan, please visit your AWS console.\n\nThank you for using AWS!',
          body_preview: 'Your AWS contract is up for renewal on January 15, 2026. Current plan Enterprise...',
          received_time: 'Dec 28, 2025 at 10:00 AM',
          received_datetime: '2025-12-28T10:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-wdj-4',
          thread_id: 'thread-w-4',
          subject: 'Sprint Planning Meeting - Next Week',
          from: 'Scrum Master <scrum@devteam.com>',
          sender: 'scrum@devteam.com',
          sender_name: 'Scrum Master',
          to: 'wdjwood@gmail.com',
          body: 'Hi Team,\n\nSprint planning for Sprint 24 is scheduled for Monday at 9 AM.\n\nPlease review the backlog and come prepared with your estimates.\n\nSee you there!',
          body_preview: 'Sprint planning for Sprint 24 is scheduled for Monday at 9 AM...',
          received_time: 'Dec 27, 2025 at 3:00 PM',
          received_datetime: '2025-12-27T15:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-wdj-5',
          thread_id: 'thread-w-5',
          subject: 'GitHub Security Alert - Dependency Update Required',
          from: 'GitHub <noreply@github.com>',
          sender: 'noreply@github.com',
          sender_name: 'GitHub',
          to: 'wdjwood@gmail.com',
          body: 'Security Alert: Your repository has a dependency with a known vulnerability.\n\nAffected: lodash@4.17.15\nSeverity: High\nRecommended: Update to lodash@4.17.21\n\nView details in GitHub Security tab.',
          body_preview: 'Security Alert: Your repository has a dependency with a known vulnerability. Affected lodash...',
          received_time: 'Dec 27, 2025 at 11:00 AM',
          received_datetime: '2025-12-27T11:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-wdj-6',
          thread_id: 'thread-w-6',
          subject: 'Freelance Opportunity - React Native Project',
          from: 'Hiring Manager <jobs@techstartup.com>',
          sender: 'jobs@techstartup.com',
          sender_name: 'Hiring Manager',
          to: 'wdjwood@gmail.com',
          body: 'We are looking for a React Native developer for a 3-month contract project.\n\nRate: $100/hour\nStart: February 2026\nRemote: Yes\n\nInterested? Let me know!',
          body_preview: 'We are looking for a React Native developer for a 3-month contract project. Rate $100/hour...',
          received_time: 'Dec 26, 2025 at 5:00 PM',
          received_datetime: '2025-12-26T17:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        }
      ]
    },
    creatrverse: {
      inbox: [
        {
          id: 'mock-cv-1',
          thread_id: 'thread-cv-1',
          subject: 'New Customer Signup - Enterprise Plan',
          from: 'Sales Notifications <sales@creatrverse.com>',
          sender: 'sales@creatrverse.com',
          sender_name: 'Sales Notifications',
          to: 'creatrverse@gmail.com',
          body: 'Great news! A new enterprise customer has signed up:\n\nCompany: TechCorp Inc.\nPlan: Enterprise Annual\nValue: $50,000\n\nPlease reach out to onboard them properly.',
          body_preview: 'Great news! A new enterprise customer has signed up: TechCorp Inc...',
          received_time: 'Dec 29, 2025 at 12:00 PM',
          received_datetime: '2025-12-29T12:00:00Z',
          is_read: false,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-2',
          thread_id: 'thread-cv-2',
          subject: 'System Health Report - All Systems Operational',
          from: 'Monitoring <monitoring@creatrverse.com>',
          sender: 'monitoring@creatrverse.com',
          sender_name: 'Monitoring',
          to: 'creatrverse@gmail.com',
          body: 'Daily Health Report\n\nAll systems operational\nUptime: 99.98%\nResponse time: 120ms avg\nActive users: 15,234\n\nNo incidents reported.',
          body_preview: 'Daily Health Report - All systems operational, Uptime 99.98%...',
          received_time: 'Dec 29, 2025 at 8:00 AM',
          received_datetime: '2025-12-29T08:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-3',
          thread_id: 'thread-cv-3',
          subject: 'Feature Request - Dark Mode',
          from: 'Product Feedback <feedback@creatrverse.com>',
          sender: 'feedback@creatrverse.com',
          sender_name: 'Product Feedback',
          to: 'creatrverse@gmail.com',
          body: 'New Feature Request from 247 users:\n\nFeature: Dark Mode\nPriority: High\nVotes: 247\n\nTop comment: "Would love a dark mode option for late-night work sessions!"\n\nView full feedback in dashboard.',
          body_preview: 'New Feature Request from 247 users: Dark Mode with High priority...',
          received_time: 'Dec 28, 2025 at 6:00 PM',
          received_datetime: '2025-12-28T18:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-4',
          thread_id: 'thread-cv-4',
          subject: 'Monthly Revenue Report - December 2025',
          from: 'Finance Team <finance@creatrverse.com>',
          sender: 'finance@creatrverse.com',
          sender_name: 'Finance Team',
          to: 'creatrverse@gmail.com',
          body: 'December Revenue Summary:\n\nMRR: $125,000 (+12%)\nNew Customers: 48\nChurned: 5\nNet Revenue Retention: 115%\n\nQ4 on track to hit targets!\n\nFull report attached.',
          body_preview: 'December Revenue Summary: MRR $125,000, New Customers 48, Net Revenue Retention 115%...',
          received_time: 'Dec 28, 2025 at 2:00 PM',
          received_datetime: '2025-12-28T14:00:00Z',
          is_read: true,
          has_attachments: true,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-5',
          thread_id: 'thread-cv-5',
          subject: 'Infrastructure Cost Optimization Recommendations',
          from: 'DevOps Team <devops@creatrverse.com>',
          sender: 'devops@creatrverse.com',
          sender_name: 'DevOps Team',
          to: 'creatrverse@gmail.com',
          body: 'We have identified several cost optimization opportunities:\n\n1. Rightsize EC2 instances: Save $800/month\n2. Enable S3 lifecycle policies: Save $200/month\n3. Remove unused RDS snapshots: Save $150/month\n\nTotal potential savings: $1,150/month\n\nShall we proceed?',
          body_preview: 'We have identified several cost optimization opportunities: Rightsize EC2 instances, Enable S3 lifecycle...',
          received_time: 'Dec 28, 2025 at 10:00 AM',
          received_datetime: '2025-12-28T10:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-6',
          thread_id: 'thread-cv-6',
          subject: 'API Rate Limit Increase Request',
          from: 'Support Team <support@creatrverse.com>',
          sender: 'support@creatrverse.com',
          sender_name: 'Support Team',
          to: 'creatrverse@gmail.com',
          body: 'Customer "Acme Corp" has requested an API rate limit increase.\n\nCurrent: 1,000 requests/hour\nRequested: 5,000 requests/hour\nReason: Growing integration usage\n\nShould we approve this request?',
          body_preview: 'Customer Acme Corp has requested an API rate limit increase from 1,000 to 5,000 requests/hour...',
          received_time: 'Dec 27, 2025 at 4:00 PM',
          received_datetime: '2025-12-27T16:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        },
        {
          id: 'mock-cv-7',
          thread_id: 'thread-cv-7',
          subject: 'Database Backup Completed Successfully',
          from: 'Backup System <backup@creatrverse.com>',
          sender: 'backup@creatrverse.com',
          sender_name: 'Backup System',
          to: 'creatrverse@gmail.com',
          body: 'Daily Database Backup Report\n\nStatus: SUCCESS\nSize: 12.4 GB\nDuration: 14 minutes\nBackup Location: s3://backups/db-2025-12-27\n\nAll backups verified and stored securely.',
          body_preview: 'Daily Database Backup Report: SUCCESS, Size 12.4 GB, Duration 14 minutes...',
          received_time: 'Dec 27, 2025 at 2:00 AM',
          received_datetime: '2025-12-27T02:00:00Z',
          is_read: true,
          has_attachments: false,
          labels: [],
          source: 'mockup',
          account_id: accountId
        }
      ]
    }
  };

  // Return emails for the specified account and folder
  const accountEmails = baseEmails[accountId] || {};
  return accountEmails[folder] || [];
};

const EmailCenter = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Account state
  const [activeAccount, setActiveAccount] = useState('creatrhq');
  const [accountsStatus, setAccountsStatus] = useState({});
  
  // Email state
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [checkingConnection, setCheckingConnection] = useState(true);
  
  // IMAP Connection Dialog
  const [showImapDialog, setShowImapDialog] = useState(false);
  const [imapEmail, setImapEmail] = useState('');
  const [imapAppPassword, setImapAppPassword] = useState('');
  const [imapConnecting, setImapConnecting] = useState(false);
  
  // Compose state
  const [isComposing, setIsComposing] = useState(false);
  const [replyMode, setReplyMode] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  
  // Schedule email state
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduledEmails, setScheduledEmails] = useState([]);
  
  // Contacts
  const [contacts, setContacts] = useState([]);
  
  // Bulk select state
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  
  // Rate limit tracking
  const [isRateLimited, setIsRateLimited] = useState(false);
  
  // Email caching
  const emailCacheRef = useRef({});
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  // Clear old cache entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const cache = emailCacheRef.current;
      
      Object.keys(cache).forEach(key => {
        if (now - cache[key].timestamp > CACHE_DURATION) {
          delete cache[key];
          console.log(`🗑️ Cleared expired cache: ${key}`);
        }
      });
    }, CACHE_DURATION); // Run cleanup every 5 minutes
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // Mobile view state
  const [mobileView, setMobileView] = useState('list');
  
  // Keyword category filter
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Meeting state
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);

  // Gmail folders
  const gmailFolders = [
    { id: 'inbox', label: 'Inbox', icon: '📥', apiFolder: 'inbox' },
    { id: 'sent', label: 'Sent', icon: '📤', apiFolder: 'sent' },
    { id: 'drafts', label: 'Drafts', icon: '📝', apiFolder: 'drafts' },
    { id: 'starred', label: 'Starred', icon: '⭐', apiFolder: 'starred' },
    { id: 'trash', label: 'Trash', icon: '🗑️', apiFolder: 'trash' },
    { id: 'scheduled', label: 'Scheduled', icon: '⏰', apiFolder: null }
  ];

  // Keyword categories for email classification
  const keywordCategories = [
    { 
      id: 'all', 
      label: 'All Messages', 
      color: 'bg-blue-500',
      keywords: [] // No filter - shows all
    },
    { 
      id: 'meeting', 
      label: 'Meeting Requests', 
      color: 'bg-yellow-500',
      keywords: ['meeting', 'schedule', 'calendar', 'appointment', 'call', 'zoom', 'teams', 'google meet', 'availability', 'sync up', 'catch up', 'discuss', 'conference', 'webinar', 'invite']
    },
    { 
      id: 'introduction', 
      label: 'Introductions', 
      color: 'bg-green-500',
      keywords: ['introduction', 'introduce', 'meet', 'connecting', 'pleased to meet', 'nice to meet', 'intro', 'referral', 'referred', 'recommend', 'connect you with', 'putting you in touch']
    },
    { 
      id: 'urgent', 
      label: 'Urgent', 
      color: 'bg-red-500',
      keywords: ['urgent', 'asap', 'immediately', 'critical', 'emergency', 'important', 'priority', 'deadline', 'time sensitive', 'action required', 'respond immediately', 'need now']
    },
    { 
      id: 'opportunity', 
      label: 'Opportunities', 
      color: 'bg-purple-500',
      keywords: ['opportunity', 'deal', 'proposal', 'investment', 'partnership', 'collaborate', 'offer', 'prospect', 'pitch', 'venture', 'funding', 'business', 'project', 'contract', 'rfp', 'bid']
    },
    { 
      id: 'travel', 
      label: 'Travel', 
      color: 'bg-orange-500',
      keywords: ['travel', 'flight', 'hotel', 'booking', 'reservation', 'trip', 'itinerary', 'airport', 'airline', 'boarding', 'departure', 'arrival', 'vacation', 'conference travel', 'uber', 'lyft', 'rental car']
    },
    { 
      id: 'general', 
      label: 'General', 
      color: 'bg-gray-500',
      keywords: [] // Catch-all for emails that don't match other categories
    }
  ];

  // Categorize an email based on keywords
  const categorizeEmail = (email) => {
    const text = `${email.subject || ''} ${email.body || ''} ${email.body_preview || ''}`.toLowerCase();
    
    // Check each category (except 'all' and 'general')
    for (const category of keywordCategories) {
      if (category.id === 'all' || category.id === 'general') continue;
      
      for (const keyword of category.keywords) {
        if (text.includes(keyword.toLowerCase())) {
          return category.id;
        }
      }
    }
    
    return 'general'; // Default category
  };

  // Get count of emails in each category
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return emails.length;
    
    return emails.filter(email => {
      const emailCategory = categorizeEmail(email);
      return emailCategory === categoryId;
    }).length;
  };

  // Get filtered emails based on selected category
  const getFilteredEmails = () => {
    if (selectedFolder === 'scheduled') return scheduledEmails;
    if (selectedCategory === 'all') return emails;
    
    return emails.filter(email => categorizeEmail(email) === selectedCategory);
  };

  // Get current account config
  const getCurrentAccount = () => ACCOUNTS.find(a => a.id === activeAccount) || ACCOUNTS[0];
  
  // Check if current account is connected
  const isConnected = () => accountsStatus[activeAccount]?.connected || false;

  // Load contacts
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const contactsList = await contactsAPI.getAll();
        setContacts(contactsList);
      } catch (error) {
        console.error('Error loading contacts:', error);
      }
    };
    loadContacts();
  }, []);

  // Load scheduled emails
  const loadScheduledEmails = async () => {
    try {
      const scheduled = await scheduledEmailsAPI.getAll('pending');
      setScheduledEmails(scheduled);
    } catch (error) {
      console.error('Error loading scheduled emails:', error);
    }
  };

  useEffect(() => {
    loadScheduledEmails();
  }, []);

  // Check all accounts status on mount
  useEffect(() => {
    const checkAllAccounts = async () => {
      try {
        // First try OAuth status
        const response = await gmailAPI.getAccounts();
        const statusMap = {};
        
        if (response.accounts) {
          response.accounts.forEach(acc => {
            statusMap[acc.account_id] = {
              connected: acc.connected,
              email: acc.connected_email || acc.email,
              name: acc.name,
              type: 'oauth'
            };
          });
        }
        
        // Also check IMAP status for each account
        for (const acc of ACCOUNTS) {
          try {
            const imapStatus = await gmailImapAPI.getStatus(acc.id);
            if (imapStatus.connected) {
              statusMap[acc.id] = {
                connected: true,
                email: imapStatus.email,
                name: acc.name,
                type: 'imap'
              };
            }
          } catch (e) {
            // IMAP not configured for this account
          }
        }
        
        // Check Outlook2 (Twenty8) account status
        try {
          const { outlook2AuthAPI } = await import('../services/api');
          const outlook2Status = await outlook2AuthAPI.status();
          if (outlook2Status.authenticated) {
            statusMap['twenty8'] = {
              connected: true,
              email: outlook2Status.email || 'darren@twenty8holdings.com',
              name: 'Twenty8 Holdings',
              type: 'outlook2'
            };
            console.log('✅ Outlook2 account connected:', outlook2Status.email);
          }
        } catch (e) {
          console.log('Outlook2 not connected:', e.message);
        }
        
        setAccountsStatus(statusMap);
      } catch (error) {
        console.error('Error checking accounts:', error);
      } finally {
        setCheckingConnection(false);
      }
    };
    
    checkAllAccounts();
    
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      const connectedAccount = urlParams.get('account');
      if (connectedAccount) {
        setActiveAccount(connectedAccount);
        toast({
          title: '✅ Connected!',
          description: `Successfully connected ${connectedAccount} account`,
          duration: 3000
        });
      }
      // Clean URL
      window.history.replaceState({}, document.title, '/email-center');
      checkAllAccounts();
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load emails when account changes - MOCKUP MODE
  useEffect(() => {
    setIsRateLimited(false);
    loadEmails(selectedFolder);
  }, [activeAccount, selectedFolder]);

  // Load emails for current account - USING MOCKUP DATA for Gmail, REAL DATA for Outlook2
  const loadEmails = async (folder = 'inbox', forceSync = false) => {
    if (!isMountedRef.current) return;
    
    try {
      setIsSyncing(true);
      const currentAccount = ACCOUNTS.find(a => a.id === activeAccount);
      
      // Check if this is the Outlook2/Twenty8 account
      if (currentAccount?.type === 'outlook2') {
        console.log(`📧 Loading REAL ${folder} emails for Outlook2 account: ${activeAccount}`);
        
        try {
          // Import outlook2EmailAPI
          const { outlook2EmailAPI } = await import('../services/api');
          const result = await outlook2EmailAPI.syncEmails(folder, 75);
          
          if (result.emails) {
            // Transform emails to match expected format
            const transformedEmails = result.emails.map(email => ({
              id: email.id,
              thread_id: email.conversationId || email.id,
              subject: email.subject || 'No Subject',
              from: email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email,
              sender: email.from_email,
              sender_name: email.from_name || email.from_email?.split('@')[0] || 'Unknown',
              to: email.to_recipients?.join(', ') || currentAccount.email,
              body: email.body || email.body_preview || '',
              body_preview: email.body_preview || email.body?.substring(0, 150) || '',
              received_time: email.received_time || new Date(email.receivedDateTime).toLocaleString(),
              received_datetime: email.receivedDateTime || email.received_time,
              is_read: email.isRead ?? email.is_read ?? true,
              has_attachments: email.hasAttachments ?? email.has_attachments ?? false,
              labels: [],
              source: 'outlook2',
              account_id: activeAccount
            }));
            
            setEmails(transformedEmails);
            setLastSyncTime(new Date());
            setIsRateLimited(false);
            
            // Auto-select first email on desktop
            if (window.innerWidth >= 768 && transformedEmails.length > 0) {
              setSelectedEmail(transformedEmails[0]);
            }
            
            console.log(`📧 Loaded ${transformedEmails.length} real emails from Outlook2`);
          } else {
            console.warn('No emails in response:', result);
            setEmails([]);
          }
        } catch (error) {
          console.error('Error fetching Outlook2 emails:', error);
          toast({
            title: '❌ Email Sync Failed',
            description: error.message || 'Could not fetch emails from Outlook',
            variant: 'destructive'
          });
          setEmails([]);
        }
      } else {
        // Use mockup data for Gmail accounts
        console.log(`📧 Loading mockup ${folder} emails for account: ${activeAccount}`);
        
        const mockupEmails = getMockupEmails(activeAccount, folder);
        
        setEmails(mockupEmails);
        setLastSyncTime(new Date());
        setIsRateLimited(false);
        
        // Auto-select first email on desktop
        if (window.innerWidth >= 768 && mockupEmails.length > 0) {
          setSelectedEmail(mockupEmails[0]);
        }
        
        console.log(`📧 Loaded ${mockupEmails.length} mockup emails`);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      setEmails([]);
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle account change
  const handleAccountChange = (accountId) => {
    if (accountId === activeAccount) return;
    
    setActiveAccount(accountId);
    setSelectedEmail(null);
    setSelectedFolder('inbox');
    setEmails([]);
    setSelectedEmailIds([]);
    setIsBulkSelectMode(false);
  };

  // Connect account - show IMAP dialog
  const handleConnect = () => {
    const account = getCurrentAccount();
    setImapEmail(account?.email || '');
    setImapAppPassword('');
    setShowImapDialog(true);
  };
  
  // Connect via IMAP with App Password
  const handleImapConnect = async () => {
    if (!imapEmail || !imapAppPassword) {
      toast({
        title: '❌ Missing Info',
        description: 'Please enter both email and app password',
        variant: 'destructive'
      });
      return;
    }
    
    setImapConnecting(true);
    try {
      const result = await gmailImapAPI.connect(activeAccount, imapEmail, imapAppPassword);
      
      if (result.success) {
        toast({
          title: '✅ Connected!',
          description: `Successfully connected ${imapEmail}`,
          duration: 3000
        });
        
        // Update account status
        setAccountsStatus(prev => ({
          ...prev,
          [activeAccount]: {
            connected: true,
            email: imapEmail,
            name: getCurrentAccount()?.name,
            type: 'imap'
          }
        }));
        
        setShowImapDialog(false);
        setImapAppPassword('');
        
        // Fetch emails
        fetchEmails();
      }
    } catch (error) {
      console.error('IMAP connection error:', error);
      toast({
        title: '❌ Connection Failed',
        description: error.message || 'Check your email and app password',
        variant: 'destructive'
      });
    } finally {
      setImapConnecting(false);
    }
  };

  // Disconnect account
  const handleDisconnect = async () => {
    const account = getCurrentAccount();
    if (!window.confirm(`Disconnect ${account.name} (${account.email})?\n\nYou will need to reconnect to access emails.`)) {
      return;
    }
    
    try {
      await gmailAPI.disconnect(activeAccount);
      setAccountsStatus(prev => ({
        ...prev,
        [activeAccount]: { ...prev[activeAccount], connected: false }
      }));
      setEmails([]);
      setSelectedEmail(null);
      toast({
        title: '✅ Disconnected',
        description: `${account.name} disconnected successfully`,
        duration: 3000
      });
    } catch (error) {
      console.error('Disconnect error:', error);
      toast({
        title: '❌ Error',
        description: 'Failed to disconnect account',
        variant: 'destructive'
      });
    }
  };

  // Handle folder click
  const handleFolderClick = (folder) => {
    setSelectedFolder(folder.id);
    if (folder.id === 'scheduled') {
      loadScheduledEmails();
    } else {
      loadEmails(folder.apiFolder);
    }
  };

  // Handle compose
  const handleCompose = () => {
    setIsComposing(true);
    setReplyMode(null);
    setSelectedEmail(null);
    setMessageText('');
    setEmailSubject('');
    setEmailTo('');
    setEmailCc('');
    setEmailBcc('');
    setShowCcBcc(false);
    setEmailAttachments([]);
    setMobileView('detail');
  };

  // Handle reply
  const handleReply = (email, replyAll = false) => {
    setReplyMode(replyAll ? 'replyAll' : 'reply');
    setIsComposing(true);
    setMobileView('detail');
    setMessageText('');
    setEmailSubject(email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`);
    setEmailTo(email.sender || email.from);
    setEmailCc('');
    setEmailBcc('');
    setShowCcBcc(false);
    setEmailAttachments([]);
  };

  // Generate AI draft
  const generateAIDraft = async (email) => {
    setGeneratingDraft(true);
    
    try {
      const prompt = email ? 
        `You are a professional assistant. Reply to this email in 2-3 short paragraphs. Be direct and professional.

EMAIL TO REPLY TO:
From: ${email.sender_name || email.sender}
Subject: ${email.subject}
${email.body || email.body_preview || 'No content'}

Reply now (2-3 paragraphs max, no salutation or signature):` :
        `Write a professional email body. Be direct and concise. No salutation or signature.`;

      const response = await apiCall('/chat/query', {
        method: 'POST',
        body: JSON.stringify({
          message: prompt,
          provider: 'openai'
        })
      });
      
      if (response?.response) {
        setMessageText(response.response);
      }
    } catch (error) {
      console.error('AI draft error:', error);
      toast({
        title: '❌ AI Draft Failed',
        description: 'Could not generate draft',
        variant: 'destructive'
      });
    } finally {
      setGeneratingDraft(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    try {
      const processedFiles = await Promise.all(
        files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                name: file.name,
                contentType: file.type,
                size: file.size,
                contentBytes: e.target.result.split(',')[1]
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );
      
      setEmailAttachments(prev => [...prev, ...processedFiles]);
    } catch (error) {
      console.error('File processing error:', error);
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Send email
  const handleSendEmail = async () => {
    if (!messageText.trim()) {
      toast({ title: '⚠️ Please enter a message', variant: 'destructive' });
      return;
    }
    
    try {
      const toArray = emailTo.split(',').map(e => e.trim()).filter(e => e);
      const ccArray = emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [];
      const bccArray = emailBcc ? emailBcc.split(',').map(e => e.trim()).filter(e => e) : [];
      
      if (scheduleEmail && scheduledDate && scheduledTime) {
        // Schedule email
        const scheduledDateTime = `${scheduledDate}T${scheduledTime}:00`;
        await scheduledEmailsAPI.create({
          to: toArray,
          cc: ccArray,
          bcc: bccArray,
          subject: emailSubject,
          body: messageText,
          scheduled_time: scheduledDateTime,
          attachments: emailAttachments,
          account_id: activeAccount
        });
        
        toast({
          title: '📅 Email Scheduled',
          description: `Will be sent at ${new Date(scheduledDateTime).toLocaleString()}`,
          duration: 3000
        });
        
        await loadScheduledEmails();
        setSelectedFolder('scheduled');
      } else {
        // Send immediately
        await gmailAPI.send({
          to: toArray,
          cc: ccArray,
          bcc: bccArray,
          subject: emailSubject,
          body: messageText
        }, activeAccount);
        
        const account = getCurrentAccount();
        toast({
          title: '✅ Email Sent',
          description: `Sent from ${account.name}`,
          duration: 3000
        });
      }
      
      // Reset compose state
      setIsComposing(false);
      setReplyMode(null);
      setMessageText('');
      setEmailSubject('');
      setEmailTo('');
      setEmailCc('');
      setEmailBcc('');
      setEmailAttachments([]);
      setScheduleEmail(false);
      
    } catch (error) {
      console.error('Send error:', error);
      toast({
        title: '❌ Send Failed',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Delete email
  const handleDelete = async (email) => {
    try {
      await gmailAPI.delete(email.id, activeAccount);
      setEmails(emails.filter(e => e.id !== email.id));
      if (selectedEmail?.id === email.id) setSelectedEmail(null);
      toast({ title: '🗑️ Email deleted', duration: 2000 });
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: '❌ Delete failed', variant: 'destructive' });
    }
  };

  // Mark as read/unread
  const handleMarkRead = async (email, isRead) => {
    try {
      await gmailAPI.markAsRead(email.id, isRead, activeAccount);
      setEmails(emails.map(e => e.id === email.id ? { ...e, is_read: isRead } : e));
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  // Star/unstar email
  const handleStar = async (email) => {
    try {
      const newStarred = !email.labels?.includes('STARRED');
      await gmailAPI.starMessage(email.id, newStarred, activeAccount);
      setEmails(emails.map(e => {
        if (e.id === email.id) {
          const labels = newStarred 
            ? [...(e.labels || []), 'STARRED']
            : (e.labels || []).filter(l => l !== 'STARRED');
          return { ...e, labels };
        }
        return e;
      }));
    } catch (error) {
      console.error('Star error:', error);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedEmailIds.length === 0) return;
    
    try {
      await Promise.all(selectedEmailIds.map(id => gmailAPI.delete(id, activeAccount)));
      setEmails(emails.filter(e => !selectedEmailIds.includes(e.id)));
      setSelectedEmailIds([]);
      setIsBulkSelectMode(false);
      toast({ title: `🗑️ ${selectedEmailIds.length} emails deleted`, duration: 2000 });
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast({ title: '❌ Delete failed', variant: 'destructive' });
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Clean email preview
  const cleanEmailPreview = (text) => {
    if (!text) return '';
    return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 100);
  };

  // Load existing meetings for "Add to Meeting" modal
  const loadExistingMeetings = async () => {
    setLoadingMeetings(true);
    try {
      const meetings = await meetingsAPI.getAll();
      setExistingMeetings(meetings || []);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setLoadingMeetings(false);
    }
  };

  // Create new meeting from email
  const handleCreateNewMeeting = async (email) => {
    try {
      const meetingData = {
        title: `Meeting: ${email.subject}`,
        date: new Date().toISOString().split('T')[0],
        time: '09:00',
        duration: 60,
        participants: [email.sender_name || email.sender],
        notes: `Created from email:\n\nFrom: ${email.sender_name || email.sender}\nSubject: ${email.subject}\n\n${email.body || email.body_preview || ''}`,
        status: 'scheduled',
        source: 'email',
        email_id: email.id
      };
      
      await meetingsAPI.create(meetingData);
      toast({
        title: '✅ Meeting Created',
        description: 'New meeting created from email',
        duration: 3000
      });
      navigate('/meetings');
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast({
        title: '❌ Error',
        description: 'Failed to create meeting',
        variant: 'destructive'
      });
    }
  };

  // Add email to existing meeting
  const handleAddToMeeting = async (meetingId) => {
    if (!selectedEmail) return;
    
    try {
      // Get existing meeting
      const meeting = existingMeetings.find(m => m.id === meetingId);
      if (!meeting) throw new Error('Meeting not found');
      
      // Append email content to meeting notes
      const emailContent = `\n\n--- Email Added ---\nFrom: ${selectedEmail.sender_name || selectedEmail.sender}\nSubject: ${selectedEmail.subject}\nDate: ${new Date(selectedEmail.received_datetime).toLocaleString()}\n\n${selectedEmail.body || selectedEmail.body_preview || ''}`;
      
      await meetingsAPI.update(meetingId, {
        notes: (meeting.notes || '') + emailContent
      });
      
      setShowMeetingModal(false);
      toast({
        title: '✅ Added to Meeting',
        description: `Email added to "${meeting.title}"`,
        duration: 3000
      });
    } catch (error) {
      console.error('Error adding to meeting:', error);
      toast({
        title: '❌ Error',
        description: 'Failed to add email to meeting',
        variant: 'destructive'
      });
    }
  };

  // Open add to meeting modal
  const openAddToMeetingModal = () => {
    loadExistingMeetings();
    setShowMeetingModal(true);
  };

  const account = getCurrentAccount();
  const filteredEmails = getFilteredEmails();

  return (
    <div className="h-screen flex flex-col p-2 lg:p-4 bg-transparent overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 lg:mb-4 flex-shrink-0">
        <h1 className="text-xl lg:text-2xl font-bold text-white">Email Center</h1>
        <div className="flex items-center gap-2">
          {isConnected() ? (
            <>
              <button
                onClick={() => loadEmails(selectedFolder, true)}
                disabled={isSyncing}
                className="flex items-center gap-2 px-3 py-2 bg-black hover:bg-white/5 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">{isSyncing ? 'Syncing...' : 'Sync'}</span>
              </button>
              <button
                onClick={handleCompose}
                className="flex items-center gap-2 px-3 py-2 bg-black hover:bg-white/10 text-white rounded-lg transition-colors"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">Compose</span>
              </button>
              <button
                onClick={() => {
                  setIsBulkSelectMode(!isBulkSelectMode);
                  setSelectedEmailIds([]);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isBulkSelectMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-black hover:bg-white/5'
                } text-white`}
              >
                <CheckCircle size={18} />
                <span className="hidden sm:inline">{isBulkSelectMode ? 'Cancel' : 'Select'}</span>
              </button>
              <button
                onClick={handleDisconnect}
                className="flex items-center gap-2 px-3 py-2 bg-transparent hover:bg-white/5 text-white rounded-lg transition-colors"
              >
                <X size={18} />
                <span className="hidden sm:inline">Disconnect</span>
              </button>
            </>
          ) : (
            <button
              onClick={handleConnect}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-black rounded-lg transition-colors"
            >
              <Cloud size={18} />
              Connect {account.name}
            </button>
          )}
        </div>
      </div>

      {/* Account Tabs */}
      <div className="mb-3 lg:mb-4 flex items-center gap-2 border-b border-white/60 overflow-x-auto flex-shrink-0">
        {ACCOUNTS.map(acc => (
          <button
            key={acc.id}
            onClick={() => handleAccountChange(acc.id)}
            className={`px-4 py-3 font-medium transition-all whitespace-nowrap ${
              activeAccount === acc.id
                ? `text-${acc.color}-400 border-b-2 border-${acc.color}-400`
                : 'text-white hover:text-white/80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail size={16} />
              <span className="text-sm">{acc.name}</span>
              {accountsStatus[acc.id]?.connected && (
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Connection Status */}
      {isConnected() && (
        <div className="mb-3 lg:mb-4 bg-transparent border border-white/60 rounded-2xl p-2 lg:p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              <span className="text-xs lg:text-sm text-white">{account.name} Connected:</span>
              <span className="text-xs lg:text-sm text-white font-medium">{accountsStatus[activeAccount]?.email}</span>
            </div>
            <div className="flex items-center gap-3">
              {lastSyncTime && (
                <span className="text-xs text-white/70">
                  Last synced: {lastSyncTime.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => loadEmails(selectedFolder, true)}
                disabled={isSyncing}
                className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Force refresh emails"
              >
                <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                <span className="text-xs">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {checkingConnection ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw size={32} className="animate-spin text-white" />
        </div>
      ) : !isConnected() ? (
        <div className="flex flex-col items-center justify-center h-64 bg-transparent border border-white/60 rounded-2xl">
          <Mail size={64} className="text-white mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Connect {account.name}</h3>
          <p className="text-white mb-4">{account.email}</p>
          <button
            onClick={handleConnect}
            className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-100 text-black rounded-lg transition-colors"
          >
            <Cloud size={20} />
            Connect Account
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 flex-1 overflow-hidden min-h-0">
          {/* Folders & Categories Sidebar */}
          <div className="hidden lg:flex lg:flex-col lg:col-span-2 border border-white/60 bg-transparent rounded-lg p-3 overflow-hidden">
            {/* Gmail Folders */}
            <h3 className="text-xs font-bold text-white mb-2 uppercase">Folders</h3>
            <div className="space-y-1 mb-4">
              {gmailFolders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => {
                    handleFolderClick(folder);
                    setSelectedCategory('all'); // Reset category when changing folder
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedFolder === folder.id
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span>{folder.icon}</span>
                  <span>{folder.label}</span>
                </button>
              ))}
            </div>

            {/* Keyword Categories */}
            <h3 className="text-xs font-bold text-white mb-2 uppercase">Categories</h3>
            <div className="space-y-1 flex-1 overflow-y-auto">
              {keywordCategories.map(category => {
                const count = getCategoryCount(category.id);
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${category.color}`}></span>
                      <span>{category.label}</span>
                    </div>
                    {count > 0 && (
                      <span className={`${category.color} text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Email List */}
          <div className={`lg:col-span-4 border border-white/60 bg-transparent rounded-lg overflow-hidden flex flex-col ${
            mobileView === 'detail' ? 'hidden lg:flex' : 'flex'
          }`}>
            {/* Mobile Category Pills */}
            <div className="lg:hidden p-2 border-b border-white/20 overflow-x-auto">
              <div className="flex gap-2">
                {keywordCategories.map(category => {
                  const count = getCategoryCount(category.id);
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                        selectedCategory === category.id
                          ? `${category.color} text-white`
                          : 'bg-white/10 text-white/70'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${selectedCategory === category.id ? 'bg-white' : category.color}`}></span>
                      <span>{category.label}</span>
                      {count > 0 && (
                        <span className={`${selectedCategory === category.id ? 'bg-white/30' : 'bg-white/20'} px-1.5 rounded-full`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bulk Actions */}
            {isBulkSelectMode && selectedEmailIds.length > 0 && (
              <div className="p-2 bg-orange-600/20 border-b border-white/20 flex items-center gap-2">
                <span className="text-white text-sm">{selectedEmailIds.length} selected</span>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
                <button
                  onClick={() => setSelectedEmailIds([])}
                  className="text-white/70 hover:text-white text-xs"
                >
                  Clear
                </button>
              </div>
            )}
            
            {/* Email List */}
            <div className="flex-1 overflow-y-auto">
              {filteredEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8">
                  {isRateLimited ? (
                    <>
                      <Clock size={64} className="text-orange-500 mb-4" />
                      <p className="text-xl font-semibold text-white mb-2">Rate Limit Exceeded</p>
                      <p className="text-white/60 text-center max-w-md mb-4">
                        The <span className="font-semibold">{getCurrentAccount().name}</span> account has reached Gmail&apos;s API quota limit.
                      </p>
                      <div className="bg-white/5 border border-white/20 rounded-lg p-4 max-w-md">
                        <p className="text-sm text-white/70 mb-3">
                          <strong className="text-white">Why this happens:</strong><br/>
                          Gmail limits API requests to prevent abuse. Free tier allows ~250-500 requests per day per account.
                        </p>
                        <p className="text-sm text-white/70">
                          <strong className="text-white">What you can do:</strong>
                        </p>
                        <ul className="text-sm text-white/70 ml-4 mt-1 space-y-1">
                          <li>• Wait 24 hours for quota to reset</li>
                          <li>• Switch to another account (top of sidebar)</li>
                          <li>• Request higher quota from Google Cloud Console</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <Mail size={48} className="text-white/40 mb-4" />
                      <p className="text-white/60">
                        {selectedCategory !== 'all' 
                          ? `No emails in ${keywordCategories.find(c => c.id === selectedCategory)?.label || selectedCategory}`
                          : `No emails in ${selectedFolder}`
                        }
                      </p>
                    </>
                  )}
                </div>
              ) : (
                filteredEmails.map(email => (
                  <div
                    key={email.id}
                    onClick={() => {
                      if (isBulkSelectMode) {
                        setSelectedEmailIds(prev =>
                          prev.includes(email.id)
                            ? prev.filter(id => id !== email.id)
                            : [...prev, email.id]
                        );
                      } else {
                        setSelectedEmail(email);
                        setMobileView('detail');
                        if (!email.is_read) handleMarkRead(email, true);
                      }
                    }}
                    className={`p-3 border-b border-white/10 cursor-pointer transition-colors ${
                      selectedEmail?.id === email.id ? 'bg-white/10' : 'hover:bg-white/5'
                    } ${!email.is_read ? 'bg-white/5' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      {isBulkSelectMode && (
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          selectedEmailIds.includes(email.id)
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-white/40'
                        }`}>
                          {selectedEmailIds.includes(email.id) && (
                            <CheckCircle size={14} className="text-white" />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm truncate ${!email.is_read ? 'font-semibold text-white' : 'text-white/80'}`}>
                            {email.sender_name || email.sender}
                          </span>
                          <span className="text-xs text-white/50">{formatDate(email.received_datetime)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate flex-1 ${!email.is_read ? 'font-medium text-white' : 'text-white/70'}`}>
                            {email.subject}
                          </p>
                          {/* Dot indicators: unread dot (white) + category dot, or just category dot if read */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!email.is_read && (
                              <span className="w-2 h-2 rounded-full bg-white" title="Unread"></span>
                            )}
                            {(() => {
                              const cat = keywordCategories.find(c => c.id === categorizeEmail(email));
                              return cat && cat.id !== 'all' ? (
                                <span className={`${cat.color} w-2 h-2 rounded-full`} title={cat.label}></span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        <p className="text-xs text-white/50 truncate mt-1">
                          {cleanEmailPreview(email.body_preview || email.body)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Email Detail / Compose */}
          <div className={`lg:col-span-6 border border-white/60 bg-transparent rounded-lg overflow-hidden flex flex-col ${
            mobileView === 'list' ? 'hidden lg:flex' : 'flex'
          }`}>
            {isComposing ? (
              /* Compose View */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-white/20 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">
                    {replyMode ? (replyMode === 'replyAll' ? 'Reply All' : 'Reply') : 'New Email'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsComposing(false);
                      setReplyMode(null);
                      setMobileView('list');
                    }}
                    className="text-white/70 hover:text-white"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  <div>
                    <label className="text-xs text-white/60">To</label>
                    <input
                      type="text"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      className="w-full bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/60"
                      placeholder="recipient@email.com"
                    />
                  </div>
                  
                  {showCcBcc && (
                    <>
                      <div>
                        <label className="text-xs text-white/60">Cc</label>
                        <input
                          type="text"
                          value={emailCc}
                          onChange={(e) => setEmailCc(e.target.value)}
                          className="w-full bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/60">Bcc</label>
                        <input
                          type="text"
                          value={emailBcc}
                          onChange={(e) => setEmailBcc(e.target.value)}
                          className="w-full bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/60"
                        />
                      </div>
                    </>
                  )}
                  
                  <button
                    onClick={() => setShowCcBcc(!showCcBcc)}
                    className="text-xs text-white/60 hover:text-white"
                  >
                    {showCcBcc ? 'Hide Cc/Bcc' : 'Add Cc/Bcc'}
                  </button>
                  
                  <div>
                    <label className="text-xs text-white/60">Subject</label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      className="w-full bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/60"
                      placeholder="Subject"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-white/60">Message</label>
                      <button
                        onClick={() => generateAIDraft(selectedEmail)}
                        disabled={generatingDraft}
                        className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
                      >
                        <Sparkles size={14} />
                        {generatingDraft ? 'Generating...' : 'AI Draft'}
                      </button>
                    </div>
                    <textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="w-full h-48 bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-white/60 resize-none"
                      placeholder="Write your message..."
                    />
                  </div>
                  
                  {/* Attachments */}
                  {emailAttachments.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-xs text-white/60">Attachments</label>
                      {emailAttachments.map((att, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white/10 rounded px-2 py-1">
                          <span className="text-xs text-white truncate">{att.name}</span>
                          <button
                            onClick={() => setEmailAttachments(prev => prev.filter((_, i) => i !== idx))}
                            className="text-white/60 hover:text-white"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Schedule option */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="schedule"
                      checked={scheduleEmail}
                      onChange={(e) => setScheduleEmail(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="schedule" className="text-sm text-white/70">Schedule for later</label>
                  </div>
                  
                  {scheduleEmail && (
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="flex-1 bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm"
                      />
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="flex-1 bg-transparent border border-white/30 rounded px-3 py-2 text-white text-sm"
                      />
                    </div>
                  )}
                </div>
                
                <div className="p-3 border-t border-white/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1 px-3 py-2 text-white/70 hover:text-white text-sm"
                    >
                      <Paperclip size={16} />
                      Attach
                    </button>
                  </div>
                  <button
                    onClick={handleSendEmail}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Send size={16} />
                    {scheduleEmail ? 'Schedule' : 'Send'}
                  </button>
                </div>
              </div>
            ) : selectedEmail ? (
              /* Email Detail View */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-white/20 flex items-center justify-between">
                  <button
                    onClick={() => {
                      setSelectedEmail(null);
                      setMobileView('list');
                    }}
                    className="lg:hidden text-white/70 hover:text-white"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleReply(selectedEmail)}
                      className="flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-sm rounded"
                    >
                      <MessageSquare size={14} />
                      Reply
                    </button>
                    {/* Meeting buttons */}
                    <button
                      onClick={() => handleCreateNewMeeting(selectedEmail)}
                      className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded"
                    >
                      <Calendar size={14} />
                      New Meeting
                    </button>
                    <button
                      onClick={openAddToMeetingModal}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
                    >
                      <Video size={14} />
                      Add to Meeting
                    </button>
                    <button
                      onClick={() => handleStar(selectedEmail)}
                      className={`p-2 rounded transition-colors ${
                        selectedEmail.labels?.includes('STARRED')
                          ? 'text-yellow-400'
                          : 'text-white/50 hover:text-white'
                      }`}
                    >
                      <Flag size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedEmail)}
                      className="p-2 text-white/50 hover:text-red-400 rounded transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <h2 className="text-lg font-semibold text-white mb-3">{selectedEmail.subject}</h2>
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-medium">
                      {(selectedEmail.sender_name || selectedEmail.sender || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{selectedEmail.sender_name || selectedEmail.sender}</p>
                      <p className="text-xs text-white/50">{selectedEmail.sender}</p>
                    </div>
                    <span className="text-xs text-white/50">
                      {new Date(selectedEmail.received_datetime).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-white/80 whitespace-pre-wrap text-sm">
                    {selectedEmail.body || selectedEmail.body_preview || `From: ${selectedEmail.sender_name || selectedEmail.sender}\nSubject: ${selectedEmail.subject}\nDate: ${new Date(selectedEmail.received_datetime).toLocaleString()}\n\nThis email's content is not available in preview mode. Please click to load full message content.`}
                  </div>
                </div>
              </div>
            ) : (
              /* Empty State */
              <div className="flex flex-col items-center justify-center h-full">
                <Mail size={64} className="text-white/20 mb-4" />
                <p className="text-white/40">Select an email to view</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add to Meeting Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden border border-white/20">
            <div className="p-4 border-b border-white/20 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Select Meeting</h3>
              <button
                onClick={() => setShowMeetingModal(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loadingMeetings ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw size={24} className="animate-spin text-white/50" />
                </div>
              ) : existingMeetings.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar size={48} className="text-white/30 mx-auto mb-3" />
                  <p className="text-white/60">No existing meetings found</p>
                  <button
                    onClick={() => {
                      setShowMeetingModal(false);
                      handleCreateNewMeeting(selectedEmail);
                    }}
                    className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                  >
                    Create New Meeting Instead
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {existingMeetings.map(meeting => (
                    <button
                      key={meeting.id}
                      onClick={() => handleAddToMeeting(meeting.id)}
                      className="w-full p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">{meeting.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          meeting.status === 'completed' ? 'bg-green-600/30 text-green-400' :
                          meeting.status === 'scheduled' ? 'bg-blue-600/30 text-blue-400' :
                          'bg-gray-600/30 text-gray-400'
                        }`}>
                          {meeting.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-white/50">
                        <span>{meeting.date}</span>
                        {meeting.time && <span>{meeting.time}</span>}
                        {meeting.participants?.length > 0 && (
                          <span>{meeting.participants.length} participants</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* IMAP Connection Dialog */}
      {showImapDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Key size={20} />
                Connect Email
              </h3>
              <button
                onClick={() => setShowImapDialog(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-white/70 text-sm mb-4">
              Connect using an App Password for secure access without OAuth.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Email Address</label>
                <input
                  type="email"
                  value={imapEmail}
                  onChange={(e) => setImapEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white"
                  placeholder="your@gmail.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-1">App Password</label>
                <input
                  type="password"
                  value={imapAppPassword}
                  onChange={(e) => setImapAppPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white"
                  placeholder="xxxx xxxx xxxx xxxx"
                />
                <p className="text-xs text-white/50 mt-1">
                  Generate at: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">myaccount.google.com/apppasswords</a>
                </p>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-sm">
                  <strong>How to get an App Password:</strong><br />
                  1. Enable 2-Factor Authentication on your Google account<br />
                  2. Go to App Passwords in Google Account settings<br />
                  3. Create a new app password for "Mail"<br />
                  4. Copy the 16-character password here
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowImapDialog(false)}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImapConnect}
                  disabled={imapConnecting || !imapEmail || !imapAppPassword}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {imapConnecting ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Connect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCenter;
